import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, CheckIcon } from './icons';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  /** Currently selected option value. */
  value: string;
  onChange: (value: string) => void;
  /** Optional leading icon shown in the trigger. */
  icon?: React.ReactNode;
  /** Small muted prefix, e.g. "Регион". */
  label?: string;
  /** Accessible name (falls back to label). */
  ariaLabel?: string;
  /** Style the trigger as "active" (a non-default value is selected). */
  active?: boolean;
  /** Draw a brand halo to pull attention here (e.g. the activation nudge). */
  attention?: boolean;
  /** Menu edge alignment relative to the trigger. */
  align?: 'left' | 'right';
  /**
   * Visual treatment of the trigger:
   * - `chip` (default): compact rounded-pill, inline label, sits in a wrap row.
   * - `field`: on mobile a full-width `rounded-md` field with the label stacked
   *   above (matching the text/number/date inputs so the whole filter section
   *   reads as one unit); at `md`+ it collapses back to the `chip` pill.
   */
  variant?: 'chip' | 'field';
  /** Field variant only: fill the container width on desktop too (for grids),
   *  instead of collapsing to an auto-width pill at md+. */
  fullWidth?: boolean;
  className?: string;
}

/**
 * Reusable accessible dropdown (custom popover, not a native <select>).
 * Keyboard: Enter/Space/↓ to open, ↑/↓ to move, Enter to pick, Esc to close.
 * Closes on outside click. Styled to the m16 design system.
 */
export function Dropdown({
  options,
  value,
  onChange,
  icon,
  label,
  ariaLabel,
  active = false,
  attention = false,
  align = 'left',
  variant = 'chip',
  fullWidth = false,
  className = '',
}: DropdownProps) {
  const isField = variant === 'field';
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listId = useId();

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const selected = options[selectedIndex];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // When opening, highlight the selected option and focus it.
  useEffect(() => {
    if (open) {
      setHighlight(selectedIndex);
      const id = requestAnimationFrame(() => optionRefs.current[selectedIndex]?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open, selectedIndex]);

  function choose(v: string) {
    onChange(v);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(highlight + 1, options.length - 1);
      setHighlight(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(highlight - 1, 0);
      setHighlight(prev);
      optionRefs.current[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
      optionRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = options.length - 1;
      setHighlight(last);
      optionRefs.current[last]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  // Shared active/hover state colouring (identical across variants so an active
  // field and an active pill read the same).
  const stateClass =
    active || open
      ? 'border-brand/30 bg-brand-dim text-brand'
      : 'border-edge bg-bone-surface text-ink-soft hover:border-brand/40 hover:bg-brand-dim hover:text-brand';

  return (
    <div
      ref={rootRef}
      className={`relative ${
        isField
          ? fullWidth
            ? 'block w-full'
            : 'block w-full md:inline-block md:w-auto'
          : 'inline-block'
      } ${className}`}
    >
      {/* Stacked field label (mobile only) — mirrors the input labels above. */}
      {isField && label && (
        <span className="mb-1 block text-[11px] font-medium text-ink-muted md:hidden">{label}</span>
      )}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        // When a visible `label` is shown, the visible text already names the
        // control, so we must NOT override it with an aria-label that omits the
        // selected value (label-content-name-mismatch). Only set aria-label for
        // icon-only / label-less triggers.
        aria-label={label ? undefined : ariaLabel}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        className={`${
          isField
            ? `flex h-11 w-full items-center gap-1.5 rounded-md border px-3 text-[16px] font-medium transition-colors md:h-9 md:rounded-md md:text-[13px] ${
                fullWidth ? 'md:flex md:w-full' : 'md:inline-flex md:w-auto'
              } ${stateClass}`
            : `inline-flex h-9 items-center gap-1.5 rounded-pill border px-3 text-[12.5px] font-medium transition-colors sm:h-8 ${stateClass}`
        }${attention ? ' border-orange ring-[3px] ring-orange/30' : ''}`}
      >
        {icon && (
          <span
            className={`shrink-0 opacity-60 ${isField ? 'h-[14px] w-[14px] md:h-[11px] md:w-[11px]' : 'h-[11px] w-[11px]'}`}
          >
            {icon}
          </span>
        )}
        {/* Inline label: always for a chip; only at md+ for a field (where the
            stacked label already names it on mobile). */}
        {label && <span className={isField ? 'hidden md:inline' : undefined}>{label}:</span>}
        <span
          className={`font-semibold text-ink ${isField ? 'min-w-0 flex-1 truncate text-left md:flex-none' : ''}`}
        >
          {selected?.label}
        </span>
        <ChevronDown
          className={`h-2.5 w-2.5 shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''} ${
            isField ? 'ml-auto md:ml-0' : ''
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          id={listId}
          aria-activedescendant={`${listId}-opt-${highlight}`}
          onKeyDown={onMenuKeyDown}
          className={`absolute z-30 mt-1.5 max-h-[320px] overflow-y-auto rounded-lg border border-edge bg-bone-surface p-1 shadow-m-md ${
            isField ? 'w-full min-w-[200px] md:w-auto' : 'min-w-[200px]'
          } ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {options.map((o, i) => {
            const isSelected = o.value === value;
            return (
              <button
                key={o.value}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                id={`${listId}-opt-${i}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => choose(o.value)}
                onMouseEnter={() => setHighlight(i)}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] outline-none transition-colors sm:py-[7px] ${
                  isSelected ? 'font-semibold text-brand' : 'text-ink-soft'
                } ${i === highlight ? 'bg-brand-dim' : 'hover:bg-bone'}`}
              >
                <span className="flex h-3 w-3 shrink-0 items-center justify-center text-brand">
                  {isSelected && <CheckIcon className="h-3 w-3" />}
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
